/**
 * @retail-ops/api-client/http
 *
 * Base HTTP transport.
 *
 * Enforces the 10 rules from teams-app-wiring §2.5 + api-wiring §5.5:
 *   1. NEVER ?code= query — always x-functions-key header
 *   2. Retry once after 2s on /accounts/* when `success:false`; jxrl/signing/v3 no retry (opt-in)
 *   3. Error body parsed into ApiError{category, code, message, details, retryAfter}
 *   4. AbortSignal supported on every call
 *   5. W3C traceparent on every request
 *   6. X-Correlation-Id on every request
 *   7. x-pcb-* context headers injected when set
 *   8. Function-key header only (never query)
 *   9. `/templates/compile` PascalCase → camelCase normalized
 *  10. Bearer token injected when getToken provided
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import { ApiError, buildApiError, classifyError } from './errors.js';

// ---------------------------------------------------------------------------
// Config + context headers
// ---------------------------------------------------------------------------

export interface ApiHttpClientConfig {
  /** e.g. `https://pcbaccountopeningapi.azurewebsites.net/api` (no trailing slash). */
  baseUrl: string;
  /**
   * Function key sent in x-functions-key on every request. Optional — omit (or
   * pass empty) for bearer-only clients so NO function key ships in the browser
   * bundle. Consumers that still rely on key auth pass a real value.
   */
  functionKey?: string;
  /** Returns bearer token; injected when delegated auth is needed. Optional. */
  getToken?: () => Promise<string | undefined> | string | undefined;
  /** X-PCB-Client-Version identifier (e.g. ROP-teams@3.0.0). */
  version?: string;
  /** Optional callback invoked for every ApiError. */
  onError?: (err: ApiError) => void;
  /**
   * Optional callback invoked when the client transparently retries a
   * request (currently fires for 502/503/504 responses on /accounts/* and
   * /jxrl/* — the cold-start jXchange auth-failed signature). Consumers
   * wire a toast like "Reconnecting to Core Director… give it a moment"
   * to keep the FSR aware that something is happening. Auto-dismiss the
   * toast on the next successful resolve.
   */
  onRetry?: (info: { path: string; httpStatus: number; attempt: number; reason: 'transient-5xx' }) => void;
  /**
   * Well-known context headers — all optional. Any string value is forwarded as
   * an `x-pcb-*` header. `undefined`/empty skipped.
   */
  context?: () => PcbContextHeaders | Promise<PcbContextHeaders>;
  /** Optional override for fetch (e.g. node-fetch in tests). */
  fetch?: typeof fetch;
  /** Optional logger for observability. Fire-and-forget. */
  logger?: {
    debug?: (msg: string, meta?: Record<string, unknown>) => void;
    warn?: (msg: string, meta?: Record<string, unknown>) => void;
    error?: (msg: string, meta?: Record<string, unknown>) => void;
  };
}

export interface PcbContextHeaders {
  sessionId?: string;
  cartId?: string;
  cifId?: string;
  officerCode?: string;
  branchCode?: string;
  moduleCode?: string;
  userRole?: string;
  clientId?: string;
}

// ---------------------------------------------------------------------------
// Internal request options
// ---------------------------------------------------------------------------

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface InternalRequestOptions extends RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Override context for this call (e.g. anonymous webhook). */
  contextOverride?: Partial<PcbContextHeaders> | null;
  /** Default false; /accounts/* path is auto-enabled unless explicitly disabled. */
  retryOn5xx?: boolean;
  /** For `/templates/compile`: normalize PascalCase → camelCase. */
  normalizePascalToCamel?: boolean;
  /** Treat the response body as text (returned via `T=string`). */
  asText?: boolean;
}

// ---------------------------------------------------------------------------
// W3C traceparent + UUID helpers
// ---------------------------------------------------------------------------

function hex(bytes: number): string {
  const arr = new Uint8Array(bytes);
  if (typeof globalThis.crypto !== 'undefined' && globalThis.crypto.getRandomValues) {
    globalThis.crypto.getRandomValues(arr);
  } else {
    for (let i = 0; i < bytes; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  let out = '';
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i] ?? 0;
    out += v.toString(16).padStart(2, '0');
  }
  return out;
}

function newTraceparent(): { header: string; traceId: string; spanId: string } {
  const traceId = hex(16);
  const spanId = hex(8);
  // version=00, flags=01 (sampled)
  return { header: `00-${traceId}-${spanId}-01`, traceId, spanId };
}

function newUuid(): string {
  if (typeof globalThis.crypto !== 'undefined' && typeof globalThis.crypto.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const b = hex(16);
  // Format 8-4-4-4-12
  return `${b.slice(0, 8)}-${b.slice(8, 12)}-4${b.slice(13, 16)}-a${b.slice(17, 20)}-${b.slice(20, 32)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

// ---------------------------------------------------------------------------
// PascalCase → camelCase normalizer (shallow — only top-level keys)
// ---------------------------------------------------------------------------

function lowerFirst(s: string): string {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
}

export function normalizeTopLevelCamel<T = Record<string, unknown>>(obj: unknown): T {
  if (obj == null || typeof obj !== 'object' || Array.isArray(obj)) return obj as T;
  const src = obj as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(src)) {
    out[lowerFirst(key)] = src[key];
  }
  return out as T;
}

// ---------------------------------------------------------------------------
// ApiHttpClient
// ---------------------------------------------------------------------------

export class ApiHttpClient {
  public readonly config: ApiHttpClientConfig;

  public constructor(config: ApiHttpClientConfig) {
    if (!config.baseUrl || typeof config.baseUrl !== 'string') {
      throw new Error('ApiHttpClient: baseUrl is required');
    }
    // functionKey is OPTIONAL — bearer-only clients (e.g. risk-mgmt-panel) ship
    // no key. When present it must be a string; when absent the x-functions-key
    // header is simply not emitted (_buildHeaders guards on truthiness).
    if (config.functionKey !== undefined && typeof config.functionKey !== 'string') {
      throw new Error('ApiHttpClient: functionKey must be a string when provided');
    }
    this.config = {
      ...config,
      // Strip trailing slash so path joins cleanly.
      baseUrl: config.baseUrl.replace(/\/+$/, ''),
    };
  }

  // -------------------------------------------------------------------------
  // Public convenience wrappers
  // -------------------------------------------------------------------------

  public get<T>(path: string, opts: InternalRequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, opts);
  }

  public post<T>(path: string, opts: InternalRequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, opts);
  }

  public patch<T>(path: string, opts: InternalRequestOptions = {}): Promise<T> {
    return this.request<T>('PATCH', path, opts);
  }

  public put<T>(path: string, opts: InternalRequestOptions = {}): Promise<T> {
    return this.request<T>('PUT', path, opts);
  }

  public del<T>(path: string, opts: InternalRequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, opts);
  }

  /**
   * GET a binary response as a Blob (e.g. a PDF for an <iframe>/<embed>
   * preview). Reuses the same URL + auth-header pipeline as JSON requests
   * (function key + bearer + x-pcb-* context) so an iframe — which cannot send
   * custom headers — is fed a `URL.createObjectURL(blob)` instead. Throws an
   * ApiError on non-2xx. No retry.
   */
  public async getBlob(path: string, opts: InternalRequestOptions = {}): Promise<Blob> {
    const url = this._buildUrl(path, opts.query);
    const headers = await this._buildHeaders({ ...opts, headers: { accept: 'application/pdf,*/*', ...(opts.headers ?? {}) } });
    const { header: traceparent } = this._ensureTraceparent(headers);
    const correlationId = this._ensureCorrelationId(headers, opts.correlationId);

    const fetchImpl = this.config.fetch ?? globalThis.fetch;
    if (typeof fetchImpl !== 'function') {
      throw new Error('ApiHttpClient: no fetch implementation available');
    }
    const init: RequestInit = { method: 'GET', headers };
    if (opts.signal) init.signal = opts.signal;

    const response = await fetchImpl(url, init);
    if (!response.ok) {
      // The error body is JSON ({success:false, error, errorCode}) on every v3
      // route; parse it so the officer reads the server's sentence instead of
      // "HTTP 409 on GET …" (Bill 2026-10-02, customer copy / Aurora package).
      const text = await response.text().catch(() => '');
      let body: unknown = text;
      try { body = text ? JSON.parse(text) : text; } catch { /* keep the text */ }
      throw buildApiError({
        httpStatus: response.status,
        body,
        traceparent,
        correlationId,
        retryAfterHeader: response.headers.get('retry-after'),
        fallbackMessage: `HTTP ${response.status} on GET ${path}`,
      });
    }
    return await response.blob();
  }

  // -------------------------------------------------------------------------
  // Core request pipeline
  // -------------------------------------------------------------------------

  public async request<T>(method: HttpMethod, path: string, opts: InternalRequestOptions = {}): Promise<T> {
    // Default retry policy per §1.4 (Bill 2026-04-28 extension):
    //   /accounts/* : retry once after 2s on {success:false} for POST
    //   /jxrl/*     : retry once after 2s on 5xx (cold-start auth-failed
    //                 signature — JHA's ims.jhacloudservices.com DNS hiccup
    //                 on a fresh Function instance; a single retry resolves
    //                 it because the second lookup hits a warm cache)
    //   /signing/*, /v3/* : NO retry (unless explicitly opted in)
    const allowAccountsRetry =
      method === 'POST' && path.startsWith('/accounts/') && opts.retry !== false;
    const allowJxrlRetry = path.startsWith('/jxrl/') && opts.retry !== false;

    try {
      const result = await this._fetchOnce<T>(method, path, opts);
      if (allowAccountsRetry && this._isAccountsRetryCandidate(result)) {
        await sleep(2000);
        return await this._fetchOnce<T>(method, path, opts);
      }
      return result;
    } catch (e) {
      if (ApiError.isApiError(e)) {
        const isTransient5xx =
          e.httpStatus === 500 || e.httpStatus === 502 || e.httpStatus === 503 || e.httpStatus === 504;

        // /accounts/* retry on 5xx (opt-in via default policy for POST)
        if (allowAccountsRetry && isTransient5xx) {
          this._fireOnRetry(path, e.httpStatus, 2);
          await sleep(2000);
          return await this._fetchOnce<T>(method, path, opts);
        }
        // /jxrl/* retry on 5xx — covers the cold-start auth-failed case that
        // surfaces as 502 from the Function ("Auth failed: HttpRequestException:
        // No such host is known").
        if (allowJxrlRetry && isTransient5xx) {
          this._fireOnRetry(path, e.httpStatus, 2);
          await sleep(2000);
          return await this._fetchOnce<T>(method, path, opts);
        }
        if (opts.retry === true && (e.httpStatus >= 500 || e.httpStatus === 0)) {
          this._fireOnRetry(path, e.httpStatus, 2);
          await sleep(2000);
          return await this._fetchOnce<T>(method, path, opts);
        }
        if (this.config.onError) this.config.onError(e);
      }
      throw e;
    }
  }

  private _fireOnRetry(path: string, httpStatus: number, attempt: number): void {
    if (!this.config.onRetry) return;
    try {
      this.config.onRetry({ path, httpStatus, attempt, reason: 'transient-5xx' });
    } catch {
      // never let a consumer's retry-handler abort a real retry
    }
  }

  private _isAccountsRetryCandidate(body: unknown): boolean {
    if (body == null || typeof body !== 'object') return false;
    const b = body as { Success?: unknown; success?: unknown };
    return b.Success === false || b.success === false;
  }

  private async _fetchOnce<T>(method: HttpMethod, path: string, opts: InternalRequestOptions): Promise<T> {
    const url = this._buildUrl(path, opts.query);
    const headers = await this._buildHeaders(opts);

    const { header: traceparent } = this._ensureTraceparent(headers);
    const correlationId = this._ensureCorrelationId(headers, opts.correlationId);

    const fetchImpl = this.config.fetch ?? globalThis.fetch;
    if (typeof fetchImpl !== 'function') {
      throw new Error('ApiHttpClient: no fetch implementation available');
    }

    let responseStatus = 0;
    let retryAfterHeader: string | null = null;

    try {
      const init: RequestInit = {
        method,
        headers,
      };
      if (opts.body !== undefined && method !== 'GET' && method !== 'DELETE') {
        init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
      }
      if (opts.signal) init.signal = opts.signal;

      const response = await fetchImpl(url, init);
      responseStatus = response.status;
      retryAfterHeader = response.headers.get('retry-after');

      // 204 No Content — return empty
      if (response.status === 204) return undefined as unknown as T;

      // Wet-sign 422 has a DIFFERENT shape than success envelope; surface as ApiError.
      const contentType = response.headers.get('content-type') ?? '';
      const isJson = contentType.includes('application/json');

      if (opts.asText) {
        const text = await response.text();
        if (!response.ok) {
          throw buildApiError({
            httpStatus: response.status,
            body: text,
            traceparent,
            correlationId,
            retryAfterHeader,
            fallbackMessage: `HTTP ${response.status} on ${method} ${path}`,
          });
        }
        return text as unknown as T;
      }

      const body: unknown = isJson ? await response.json().catch(() => ({})) : await response.text().catch(() => '');

      if (!response.ok) {
        throw buildApiError({
          httpStatus: response.status,
          body,
          traceparent,
          correlationId,
          retryAfterHeader,
          fallbackMessage: `HTTP ${response.status} on ${method} ${path}`,
        });
      }

      // Happy path — optionally normalize PascalCase top-level
      if (opts.normalizePascalToCamel) {
        return normalizeTopLevelCamel<T>(body);
      }
      return body as T;
    } catch (e: unknown) {
      if (ApiError.isApiError(e)) throw e;
      // Network / AbortError / CORS / DNS / TLS — category = network
      const isAbort =
        typeof e === 'object' &&
        e !== null &&
        (e as { name?: string }).name === 'AbortError';
      const msg = e instanceof Error ? e.message : String(e);
      const httpStatus = responseStatus || 0;
      const category = isAbort ? 'unknown' : classifyError(httpStatus, undefined);
      throw new ApiError({
        httpStatus,
        category,
        message: msg,
        traceparent,
        correlationId,
      });
    }
  }

  // -------------------------------------------------------------------------
  // URL + header builders
  // -------------------------------------------------------------------------

  private _buildUrl(
    path: string,
    query?: Record<string, string | number | boolean | undefined | null>
  ): string {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    let url = `${this.config.baseUrl}${cleanPath}`;
    if (query) {
      const pairs: string[] = [];
      for (const [k, v] of Object.entries(query)) {
        if (v === undefined || v === null) continue;
        // Rule #1: never forward `code` query — strip if a caller accidentally adds it.
        if (k.toLowerCase() === 'code') continue;
        pairs.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
      }
      if (pairs.length > 0) {
        url += (url.includes('?') ? '&' : '?') + pairs.join('&');
      }
    }
    return url;
  }

  private async _buildHeaders(opts: InternalRequestOptions): Promise<Record<string, string>> {
    const out: Record<string, string> = {
      accept: 'application/json',
      'content-type': 'application/json',
    };
    // Only emit the function key when one is configured. Bearer-only clients
    // (e.g. risk-mgmt-panel) omit it entirely — no key in the browser bundle.
    if (this.config.functionKey) out['x-functions-key'] = this.config.functionKey;
    if (this.config.version) out['x-pcb-client-version'] = this.config.version;

    // Bearer token (optional)
    if (this.config.getToken) {
      try {
        const token = await Promise.resolve(this.config.getToken());
        if (token) out['authorization'] = `Bearer ${token}`;
      } catch {
        // token fetch failures surface as 401 at the server layer; do not block
      }
    }

    // Context headers
    let ctx: PcbContextHeaders | undefined;
    if (this.config.context) {
      try {
        ctx = await Promise.resolve(this.config.context());
      } catch {
        ctx = undefined;
      }
    }
    if (opts.contextOverride != null) ctx = { ...ctx, ...opts.contextOverride };

    if (ctx) {
      if (ctx.sessionId) out['x-pcb-session-id'] = ctx.sessionId;
      if (ctx.cartId) out['x-pcb-cart-id'] = ctx.cartId;
      if (ctx.cifId) out['x-pcb-cif-id'] = ctx.cifId;
      if (ctx.officerCode) out['x-pcb-officer-code'] = ctx.officerCode;
      if (ctx.branchCode) out['x-pcb-branch-code'] = ctx.branchCode;
      if (ctx.moduleCode) out['x-pcb-module-code'] = ctx.moduleCode;
      if (ctx.userRole) out['x-pcb-user-role'] = ctx.userRole;
      if (ctx.clientId) out['x-pcb-client-id'] = ctx.clientId;
    }

    // Caller-supplied headers (but never allow overriding the function key)
    if (opts.headers) {
      for (const [k, v] of Object.entries(opts.headers)) {
        const lower = k.toLowerCase();
        if (lower === 'x-functions-key') continue;
        out[lower] = v;
      }
    }
    return out;
  }

  private _ensureTraceparent(headers: Record<string, string>): { header: string } {
    if (headers['traceparent']) return { header: headers['traceparent'] };
    const { header } = newTraceparent();
    headers['traceparent'] = header;
    return { header };
  }

  private _ensureCorrelationId(headers: Record<string, string>, supplied?: string): string {
    const existing = headers['x-correlation-id'] ?? supplied;
    const value = existing && existing.length > 0 ? existing : newUuid();
    headers['x-correlation-id'] = value;
    return value;
  }
}

// Re-export for convenience
export { ApiError };
